// Runtime Monitor (Figma) — runtime EXECUTION MONITOR (contract §9). A fixed 2×6 grid: a leading
// simulated "Sanity Check" pre-flight box followed by the 11 scored skills. Each skill box follows
// real runtime: running/retrying = glowing gold; queued/done = grey; gated_out/failed = error.
// Driven entirely by the RunEvent stream. The Sanity Check box is cosmetic (no run events): it
// flickers gold for ~2s at run start to simulate the pre-flight transcript check.

import type { SkillRunState } from '../lib/types.js';
import { SKILL_ORDER } from '../lib/skills.js';
import { setText } from '../lib/escape.js';

export class ControlBoard {
  private boxes = new Map<string, HTMLElement>();
  private sanityBox: HTMLElement | null = null;

  constructor(private readonly root: HTMLElement) {
    this.build();
  }

  /** Build the fixed grid once (idle): the Sanity Check box first, then the 11 scored skills. */
  build(): void {
    this.root.innerHTML = '';
    this.boxes.clear();
    // Leading simulated Sanity Check box — not a scored skill, never registered for run events.
    const sanity = document.createElement('div');
    sanity.dataset.skill = 'sanity-check';
    setText(sanity, 'Sanity Check');
    sanity.className = 'cb-box cb-idle';
    this.root.appendChild(sanity);
    this.sanityBox = sanity;
    for (const s of SKILL_ORDER) {
      const box = document.createElement('div');
      box.dataset.skill = s.id;
      setText(box, s.label);
      box.className = 'cb-box cb-idle';
      this.root.appendChild(box);
      this.boxes.set(s.id, box);
    }
  }

  resetIdle(): void {
    if (this.sanityBox) this.sanityBox.className = 'cb-box cb-idle';
    for (const box of this.boxes.values()) {
      box.className = 'cb-box cb-idle';
    }
  }

  /** Cosmetic pre-flight animation: flicker the Sanity Check box gold for ~2s, then settle to done.
   *  Simulates a sanity check of the transcript; carries no real scoring weight. */
  async simulateSanityCheck(ms = 2000): Promise<void> {
    const box = this.sanityBox;
    if (!box) return;
    box.className = 'cb-box cb-running';
    await new Promise((r) => setTimeout(r, ms));
    // Only settle if a fresh run hasn't reset us back to idle in the meantime.
    if (box.classList.contains('cb-running')) box.className = 'cb-box cb-done';
  }

  set(skillId: string, state: SkillRunState): void {
    const box = this.boxes.get(skillId);
    if (!box) return;
    box.classList.remove('cb-idle', 'cb-running', 'cb-done', 'cb-error', 'cb-unavailable');
    if (state === 'running' || state === 'retrying') box.classList.add('cb-running');
    else if (state === 'done') box.classList.add('cb-done');
    else if (state === 'gated_out' || state === 'failed') box.classList.add('cb-error');
    else box.classList.add('cb-idle');
  }

  /** Terminal NON-red "couldn't be scored" state — used after autonomous retries are exhausted, so a
   *  solid red frame is never left for the user to puzzle over (they never need to click Run). */
  markUnavailable(skillId: string): void {
    const box = this.boxes.get(skillId);
    if (!box) return;
    box.className = 'cb-box cb-unavailable';
  }
}
